import $ from 'jquery';
import PropTypes from 'prop-types';
import React, { useCallback, useContext, useEffect } from 'react';
import { AlertHelper } from '@bingads-webui-campaign/alert-helper';
import { withUiBlock } from '@bingads-webui/uiblocker';
import { GridContext } from '@bingads-webui-grid/grid-context';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { ScopeContext } from '@bingads-webui-react/scope-service';
import { getRoutePathById } from '@bingads-webui-react/with-site-map';
import { setClarityTag } from '@bingads-webui-universal/clarity';
import {
  DataGrid,
  DataGridBody,
  DataGridCell,
  DataGridHeader,
  DataGridHeaderCell,
  DataGridRow,
} from '@fluentui/react-components';
import {
  getCellFocusMode,
  getColumns,
} from './config';
import { fetchCampaignsByIdEDM } from '../../fetch/campaigns';
import { deleteExperiment, fetchExperiments, stitchFullCampaignsIntoExperiments } from '../../fetch/experiment';

export const LiftStudiesGrid = ({ onClickEdit, siteMap }) => {
  const i18n = useContext(I18nContext);
  const { dispatch, state } = useContext(GridContext);
  const { scope } = useContext(ScopeContext);

  const onClickName = useCallback(({ id }) => {
    const route = getRoutePathById(siteMap, 'lift-measurement-results');
    scope.append({ liftExperimentId: id });
    scope.save(route);
  }, [scope, siteMap]);

  const onFailure = useCallback((message) => {
    const alertHelper = new AlertHelper({ i18n });
    alertHelper.showError(message);
  }, [i18n]);

  const onDeleteFailure = useCallback((message) => {
    setClarityTag({
      IsDeleteLiftExperimentSuccessful: false,
    });
    const alertHelper = new AlertHelper({ i18n });
    alertHelper.showError(message);
  }, [i18n]);

  const onDeleteSuccess = useCallback((liftExperimentIdToDelete) => {
    const liftExperimentsWithDeletedRemoved = state.items.reduce((accum, liftExperiment) => {
      if (liftExperimentIdToDelete !== liftExperiment.Id) {
        accum.push(liftExperiment);
      }
      return accum;
    }, []);
    setClarityTag({
      IsDeleteLiftExperimentSuccessful: true,
    });
    dispatch({ type: 'FINISH_LOAD_DATA', payload: { items: liftExperimentsWithDeletedRemoved, totalCount: state.totalCount - 1 } });
  }, [dispatch, state.items, state.totalCount]);

  const onClickDelete = useCallback((liftExperimentIdToDelete) => {
    setClarityTag({
      IsClickedDeleteLiftExperiment: true,
    });
    deleteExperiment(scope.values, liftExperimentIdToDelete, onDeleteSuccess, onDeleteFailure);
  }, [onDeleteSuccess, onDeleteFailure, scope]);

  const columns = getColumns({
    i18n, onClickDelete, onClickEdit, onClickName,
  });

  const onFetchExperimentsSuccess = useCallback(async (fetchedLiftExperiments) => {
    const associatedCampaignIds = fetchedLiftExperiments.reduce((accum, liftExperiment) => [...accum, liftExperiment.Campaigns], []);
    if (associatedCampaignIds && associatedCampaignIds.length > 0) {
      const campaignsResponse = await fetchCampaignsByIdEDM(scope.values, associatedCampaignIds)
        .finally(withUiBlock($('.lift-measurement-wrapper'), i18n.getString(_TL_('Loading'))), [i18n]);
      const formattedLiftExperiments = stitchFullCampaignsIntoExperiments(campaignsResponse.value, fetchedLiftExperiments);
      dispatch({ type: 'FINISH_LOAD_DATA', payload: { items: formattedLiftExperiments, totalCount: formattedLiftExperiments.length } });
    } else {
      dispatch({ type: 'FINISH_LOAD_DATA', payload: { items: fetchedLiftExperiments, totalCount: fetchedLiftExperiments.length } });
    }
  }, [dispatch, i18n, scope.values]);

  const getOrderBy = useCallback((column) => {
    if (!column.fieldName || column.fieldName === 'Status' || column.fieldName === 'Campaigns' || column.fieldName === 'actions') {
      return undefined;
    } else if (column.fieldName === 'dates') {
      return { StartDate: column.isSortDescending ? -1 : 1 };
    }

    return {
      [column.fieldName]: column.isSortDescending ? -1 : 1,
    };
  }, []);

  useEffect(() => {
    fetchExperiments(scope.values, {
      query: {
        count: true,
        top: state.pagination.pageSize,
        skip: ((state.pagination.pageNumber - 1) * state.pagination.pageSize),
        filter: { where: state.editableFilter },
        orderby: getOrderBy(state.sortedColumn),
      },
      onSuccess: onFetchExperimentsSuccess,
      onFailure,
    });
  }, [getOrderBy, onFailure, onFetchExperimentsSuccess, state.editableFilter, state.pagination, state.sortedColumn, scope.values]);

  const onSortChange = useCallback((e, { sortColumn, sortDirection }) => {
    setClarityTag({
      IsLiftExperimentSortChanged: true,
      LiftExperimentSortField: sortColumn,
      LiftExperimentSortDirection: sortDirection,
    });
    dispatch({
      type: 'UPDATE_SORTED_COLUMN',
      payload: {
        ...state.sortedColumn,
        fieldName: sortColumn,
        isSortDescending: sortDirection === 'descending',
      },
    });
  }, [dispatch, state]);

  return (
    <>
      <DataGrid
        className="lift-measurement-experiments-grid"
        columns={columns}
        getRowId={item => item.Id}
        items={state.items}
        onSortChange={onSortChange}
        sortable
      >
        <DataGridHeader>
          <DataGridRow>
            {({ renderHeaderCell }) => (
              <DataGridHeaderCell style={{ padding: '0 10px' }}>
                  {renderHeaderCell()}
              </DataGridHeaderCell>
            )}
          </DataGridRow>
        </DataGridHeader>
        <DataGridBody>
          {({ item, rowId }) => (
            <DataGridRow key={rowId}>
              {({ renderCell, columnId }) => (
                <DataGridCell focusMode={getCellFocusMode(columnId)}>
                  {renderCell(item)}
                </DataGridCell>
              )}
            </DataGridRow>
          )}
        </DataGridBody>
      </DataGrid>
    </>
  );
};

LiftStudiesGrid.propTypes = {
  onClickEdit: PropTypes.func.isRequired,
  siteMap: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
  })).isRequired,
};
